import editors from '@shared/data/editors.json'
import * as styles from './EditorByline.css'

interface Editor {
  name: string
  age: string
  hometown: string
  education: string
  experience: string[]
  image: string
}

const byName = new Map((editors as Editor[]).map((e) => [e.name, e]))

export function getEditor(name?: string): Editor | undefined {
  return name ? byName.get(name) : undefined
}

/** Derive a short role line from the first experience entry (before the first
 *  comma/semicolon), e.g. "Contributing Editor · Tucson, Arizona". */
function roleLine(editor: Editor): string {
  return editor.hometown ? `Contributing Editor · ${editor.hometown}` : 'Contributing Editor'
}

/** Compact byline: avatar + name; a structured bio card appears on hover/focus.
 *  Falls back to plain text for legacy bylines not on the roster. */
export function EditorByline({ name }: { name: string }) {
  const editor = getEditor(name)
  if (!editor) return <span className={styles.name}>{name}</span>

  return (
    <span className={styles.byline} tabIndex={0} aria-describedby={`editor-${editor.name}`}>
      <img
        src={encodeURI(editor.image)}
        alt={editor.name}
        className={styles.avatar}
        loading="lazy"
        width={28}
        height={28}
      />
      <span className={styles.name}>{editor.name}</span>

      <span role="tooltip" id={`editor-${editor.name}`} className={styles.card}>
        <span className={styles.cardHeader}>
          <img src={encodeURI(editor.image)} alt="" className={styles.cardAvatar} loading="lazy" />
          <span>
            <span className={styles.cardName}>{editor.name}</span>
            <span className={styles.cardRole}>{roleLine(editor)}</span>
          </span>
        </span>
        <span className={styles.cardBody}>
          {editor.education && (
            <span className={styles.row}>
              <span className={styles.rowLabel}>Education</span>
              <span className={styles.rowText}>{editor.education}</span>
            </span>
          )}
          {editor.experience.length > 0 && (
            <span className={styles.row}>
              <span className={styles.rowLabel}>Experience</span>
              <ul className={styles.expList}>
                {editor.experience.map((line) => (
                  <li key={line} className={styles.expItem}>{line}</li>
                ))}
              </ul>
            </span>
          )}
        </span>
      </span>
    </span>
  )
}
